import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';

function CapsuleLogo({ size = 'normal' }) {
  const sizeClass = size === 'large' ? 'capsule-icon-large' : '';
  return (
    <div className={`capsule-icon ${sizeClass}`}>
      <div className="left"></div>
      <div className="right"></div>
    </div>
  );
}

function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ username: '', password: '', shopName: '' });

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.username, loginForm.password);
      toast.success('Welcome back!');
      // Redirect admin to admin dashboard
      if (user && user.is_platform_admin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      await register(registerForm.username, registerForm.password, registerForm.shopName, 'free_trial');
      toast.success('Account created! 15-day free trial started.');
      navigate('/');
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <CapsuleLogo size="large" />
          </div>
          <h1 className="text-4xl font-heading font-bold mt-4">
            <span className="text-mint-400">Pharma</span>
            <span className="text-white">Buddy</span>
          </h1>
          <p className="text-mint-400 text-lg italic mt-2">Pharmacist's Most Reliable Partner</p>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-mint-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-mint-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Effortless Billing</h3>
              <p className="text-navy-300 text-sm">Fast & accurate. Search by brand, salt, or batch.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-mint-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-mint-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Real-time Stock</h3>
              <p className="text-navy-300 text-sm">Expiry alerts & instant tax insights.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-mint-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-mint-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Your Data is Safe</h3>
              <p className="text-navy-300 text-sm">Single-user focus. Reliable & secure.</p>
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-navy-800 rounded-lg p-4">
              <p className="text-mint-400 font-heading font-bold text-xl">Free</p>
              <p className="text-xs text-navy-400">15-Day Trial</p>
            </div>
            <div className="bg-navy-800 rounded-lg p-4 ring-2 ring-mint-500">
              <p className="text-white font-heading font-bold text-xl">₹999</p>
              <p className="text-xs text-navy-400">Pro Plan/mo</p>
            </div>
            <div className="bg-navy-800 rounded-lg p-4">
              <p className="text-white font-heading font-bold text-xl">₹9,199</p>
              <p className="text-xs text-navy-400">Pro Plus/yr</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="flex-1 flex flex-col bg-slate-100">
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md shadow-lg border-0">
            <CardHeader className="text-center pb-2">
              <div className="lg:hidden flex flex-col items-center gap-2 mb-4">
                <CapsuleLogo size="large" />
                <div>
                  <span className="text-2xl font-heading font-bold text-mint-600">Pharma</span>
                  <span className="text-2xl font-heading font-bold text-navy-900">Buddy</span>
                </div>
                <p className="text-xs text-slate-500 italic">Pharmacist's Most Reliable Partner</p>
              </div>
              <CardTitle className="text-2xl font-heading text-navy-900">Welcome</CardTitle>
              <CardDescription>Sign in to manage your pharmacy</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Start Free Trial</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-username">Username</Label>
                      <Input
                        id="login-username"
                        data-testid="login-username"
                        placeholder="Enter your username"
                        value={loginForm.username}
                        onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password"
                        type="password"
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="login-submit"
                      className="w-full bg-mint-600 hover:bg-mint-700 text-white"
                      disabled={loading}
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="bg-mint-50 border border-mint-200 rounded-lg p-3 text-center mb-4">
                      <p className="text-sm text-mint-800 font-medium">15-Day Free Trial • No Credit Card Required</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-shop">Pharmacy Name</Label>
                      <Input
                        id="register-shop"
                        data-testid="register-shop"
                        placeholder="Your pharmacy name"
                        value={registerForm.shopName}
                        onChange={(e) => setRegisterForm({ ...registerForm, shopName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-username">Username</Label>
                      <Input
                        id="register-username"
                        data-testid="register-username"
                        placeholder="Choose a username"
                        value={registerForm.username}
                        onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password"
                        type="password"
                        placeholder="Choose a password"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                      />
                    </div>
                    
                    {/* Clickwrap Agreement - DPDP Compliance */}
                    <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border">
                      <Checkbox
                        id="terms-checkbox"
                        data-testid="terms-checkbox"
                        checked={termsAccepted}
                        onCheckedChange={setTermsAccepted}
                        className="mt-0.5"
                      />
                      <Label htmlFor="terms-checkbox" className="text-xs text-slate-600 cursor-pointer leading-relaxed">
                        I agree to the{' '}
                        <Link to="/terms" target="_blank" className="text-mint-600 hover:underline font-medium">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" target="_blank" className="text-mint-600 hover:underline font-medium">
                          Privacy Policy
                        </Link>
                        . I understand my data will be processed as per the DPDP Act, 2023.
                      </Label>
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="register-submit"
                      className="w-full bg-navy-900 hover:bg-navy-800 text-white"
                      disabled={loading || !termsAccepted}
                    >
                      {loading ? 'Creating account...' : 'Start Free Trial'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        {/* Footer */}
        <footer className="p-4 text-center border-t bg-white">
          <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-mint-600 transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-mint-600 transition-colors">Terms of Service</Link>
            <span>•</span>
            <span>© 2026 PharmaBuddy</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Login;
